<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  percent: { type: Number, default: null },
  state: {
    type: String,
    default: 'in_progress',
    validator: value => ['no_data', 'in_progress', 'achieved'].includes(value),
  },
  compact: { type: Boolean, default: false },
  tooltip: { type: String, default: '' },
});

const { t } = useI18n();

// Largura da barra em degraus de 5%: o Tailwind só gera classes escritas por
// extenso, e estilo inline não é permitido no projeto.
const WIDTH_CLASSES = [
  'w-0',
  'w-[5%]',
  'w-[10%]',
  'w-[15%]',
  'w-[20%]',
  'w-[25%]',
  'w-[30%]',
  'w-[35%]',
  'w-[40%]',
  'w-[45%]',
  'w-[50%]',
  'w-[55%]',
  'w-[60%]',
  'w-[65%]',
  'w-[70%]',
  'w-[75%]',
  'w-[80%]',
  'w-[85%]',
  'w-[90%]',
  'w-[95%]',
  'w-full',
];

const COLORS = {
  ruby: {
    fill: 'bg-n-ruby-9',
    pill: 'bg-n-ruby-3 text-n-ruby-11',
    dot: 'bg-n-ruby-9',
  },
  amber: {
    fill: 'bg-n-amber-9',
    pill: 'bg-n-amber-3 text-n-amber-11',
    dot: 'bg-n-amber-9',
  },
  teal: {
    fill: 'bg-n-teal-9',
    pill: 'bg-n-teal-3 text-n-teal-11',
    dot: 'bg-n-teal-9',
  },
};

const isNoData = computed(() => props.state === 'no_data');
const isAchieved = computed(
  () => props.state === 'achieved' || props.percent >= 100
);

// Percentual inteiro sem arredondar para cima: 99,6% não pode aparecer como "100%".
const displayPercent = computed(() => Math.floor(props.percent));

// Abaixo de 50% vermelho, de 50% a 99% amarelo, 100% ou mais verde (spec 159, D7).
const color = computed(() => {
  if (isAchieved.value) return COLORS.teal;
  return props.percent >= 50 ? COLORS.amber : COLORS.ruby;
});

// Arredonda para baixo: só chega a 100% quando a meta foi de fato atingida.
const widthClass = computed(() => {
  if (isAchieved.value) return WIDTH_CLASSES[20];
  const step = Math.floor(Math.min(props.percent, 100) / 5);
  return WIDTH_CLASSES[props.percent > 0 ? Math.max(step, 1) : 0];
});
</script>

<template>
  <span v-if="isNoData" class="text-xs text-n-slate-11">
    {{ t('COMMERCIAL_GOALS.PROGRESS.NO_DATA') }}
  </span>
  <div
    v-else
    v-tooltip="tooltip"
    class="flex gap-2 min-w-0"
    :class="compact ? 'flex-col-reverse items-start gap-1.5' : 'items-center'"
  >
    <div
      role="progressbar"
      aria-valuemin="0"
      aria-valuemax="100"
      :aria-valuenow="Math.min(displayPercent, 100)"
      class="w-full h-1.5 overflow-hidden rounded-full bg-n-slate-5"
      :class="{ 'flex-1': !compact }"
    >
      <div
        class="h-full rounded-full"
        :class="[widthClass, color.fill]"
        data-test="goal-bar-fill"
      />
    </div>
    <span
      class="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap"
      :class="color.pill"
      data-test="goal-pill"
    >
      <span v-if="isAchieved" class="i-lucide-check size-3" />
      <span v-else class="rounded-full size-1.5" :class="color.dot" />
      {{
        compact
          ? t('COMMERCIAL_GOALS.PROGRESS.PERCENT_OF_GOAL', {
              percent: displayPercent,
            })
          : `${displayPercent}%`
      }}
      <template v-if="isAchieved && !compact">
        · {{ t('COMMERCIAL_GOALS.PROGRESS.ACHIEVED') }}
      </template>
    </span>
  </div>
</template>
